import React, { useState, useEffect, useCallback } from "react";
import { IoMdMenu, IoMdClose } from "react-icons/io";
import { Link, withRouter, useLocation } from "react-router-dom";

import moment from "moment";
import "react-dates/initialize";
import { DateRangePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";

import "./style.css";

function DesktopNavbar() {
  const [isOpen, setIsOpen] = useState(true);
  const [bookingStart, setBookingStart] = useState(moment(new Date()));
  const [bookingEnd, setBookingEnd] = useState(moment(new Date()));
  const [bookingFocusedInput, setBookingFocusedInput] = useState(null);

  const { pathname } = useLocation();

  useEffect(() => {
    const handleScrollClose = () => {
      if (window.scrollY < 50) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    document.addEventListener("scroll", handleScrollClose, { passive: true });

    return () => {
      document.removeEventListener("scroll", handleScrollClose);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <>
      <div className="fixed top-0 left-0 m-6 z-50 flex items-center">
        <button
          onMouseEnter={handleMouseEnter}
          aria-label="Open navigation menu"
          className="navbar-menu-button navbar-bg-color p-4 hover:shadow-xl transition-all duration-200 hover:scale-105"
        >
          <IoMdMenu className="text-4xl" />
        </button>
      </div>
      <div className={`navbar__bg-container ${isOpen ? "visible" : ""}`}>
        <nav aria-label="Main navigation">
          <ul className="xl:container navbar__content pt-20 pb-4 tracking-20 uppercase mx-auto flex justify-center items-center text-center">
            <li>
              <Link className={pathname === "/" ? "active" : undefined} to="/">
                Home
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/rooms" ? "active" : undefined}
                to="/rooms"
              >
                Rooms
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/thingstodo" ? "active" : undefined}
                to="/thingstodo"
              >
                Nantucket
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/insider-guide" ? "active" : undefined}
                to="/insider-guide"
              >
                Insider's Guide
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/dining" ? "active" : undefined}
                to="/dining"
              >
                Dining
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/about" ? "active" : undefined}
                to="/about"
              >
                Manifesto
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/getting-here" ? "active" : undefined}
                to="/getting-here"
              >
                Getting Here
              </Link>
            </li>
            <li>
              <Link
                className={
                  pathname === "/last-minute-specials" ? "active" : undefined
                }
                to="/last-minute-specials"
              >
                Last-Minute Specials
              </Link>
            </li>

            {/* <li>
              <a
                href="https://secure.thinkreservations.com/unioninn/gift-certificates"
                target="_blank"
                rel="noopener noreferrer"
              >
                Gift Certificates
              </a>
            </li> */}
          </ul>
        </nav>
        <div className="fixed mt-6 mr-6 top-0 right-0 flex flex-row gap-3 z-10 items-center">
          <div className="date-picker-wrapper">
            <DateRangePicker
              startDate={bookingStart}
              startDateId="start"
              endDate={bookingEnd}
              endDateId="end"
              onDatesChange={({ startDate, endDate }) => {
                setBookingStart(startDate);
                setBookingEnd(endDate);
              }}
              focusedInput={bookingFocusedInput}
              onFocusChange={(focusedInput) =>
                setBookingFocusedInput(focusedInput)
              }
              numberOfMonths={2}
              displayFormat="MMM DD"
              small
            />
          </div>
          <a
            href={`https://secure.thinkreservations.com/unioninn/reservations/availability?start_date=${
              bookingStart && bookingStart.format("YYYY-MM-DD")
            }&end_date=${bookingEnd && bookingEnd.format("YYYY-MM-DD")}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Book your stay at Union Inn"
            className="navbar-book-button navbar-bg-color btn-sm border-2 border-primary-blue uppercase shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            Book Now
          </a>
        </div>
      </div>
    </>
  );
}

function MobileNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  const handleCloseMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpenMenu = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <div className="fixed z-20">
      <div className="fixed p-5 top-0 left-0 right-0 flex flex-row items-center backdrop-blur-sm bg-white/90">
        <button
          className="pointer-events-auto p-3 bg-white shadow-md hover:shadow-lg transition-shadow duration-200 rounded"
          aria-label="Open navigation menu"
          onClick={handleOpenMenu}
        >
          <IoMdMenu className="icon" />
        </button>

        <a
          className="ml-auto text-xl btn bg-primary-blue text-white shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 px-6 py-3"
          href="https://secure.thinkreservations.com/unioninn/reservations/availability"
          rel="noopener noreferrer"
          target="_blank"
          aria-label="Book your stay at Union Inn"
        >
          Book Now
        </a>
      </div>

      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-30 transition-opacity duration-300"
          onClick={handleCloseMenu}
          aria-hidden="true"
        />
      )}

      {/* Side drawer */}
      <div
        className={`tracking-20 uppercase bg-white p-10 py-20 mobile-nav__container z-40 ${
          isOpen ? "visible" : ""
        }`}
        role="dialog"
        aria-label="Mobile navigation menu"
      >
        <button
          className="absolute top-0 right-0 m-8 p-3 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          onClick={handleCloseMenu}
          aria-label="Close navigation menu"
        >
          <IoMdClose className="icon text-primary-blue" />
        </button>
        <nav aria-label="Mobile navigation">
          <ul className="navbar__content stack-l text-center leading-relaxed">
            <li>
              <Link
                className={pathname === "/" ? "active" : undefined}
                to="/"
                onClick={handleCloseMenu}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/rooms" ? "active" : undefined}
                to="/rooms"
                onClick={handleCloseMenu}
              >
                Rooms
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/thingstodo" ? "active" : undefined}
                to="/thingstodo"
                onClick={handleCloseMenu}
              >
                Nantucket
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/insider-guide" ? "active" : undefined}
                to="/insider-guide"
                onClick={handleCloseMenu}
              >
                Insider's Guide
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/dining" ? "active" : undefined}
                to="/dining"
                onClick={handleCloseMenu}
              >
                Dining
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/about" ? "active" : undefined}
                to="/about"
                onClick={handleCloseMenu}
              >
                Manifesto
              </Link>
            </li>
            <li>
              <Link
                className={pathname === "/getting-here" ? "active" : undefined}
                to="/getting-here"
                onClick={handleCloseMenu}
              >
                Getting Here
              </Link>
            </li>
            <li>
              <Link
                className={
                  pathname === "/last-minute-specials" ? "active" : undefined
                }
                to="/last-minute-specials"
                onClick={handleCloseMenu}
              >
                Last-Minute Specials
              </Link>
            </li>
            {/* <li>
              <a
                href="https://secure.thinkreservations.com/unioninn/gift-certificates"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCloseMenu}
              >
                Gift Certificates
              </a>
            </li> */}
          </ul>
        </nav>
      </div>
    </div>
  );
}

const NavBar = () => {
  return (
    <React.Fragment>
      <div className="hide-on-lg">
        <DesktopNavbar />
      </div>
      <div className="block lg:hidden">
        <MobileNavbar />
      </div>
    </React.Fragment>
  );
};

export default withRouter(NavBar);
